import {create} from 'zustand';
import {devtools, persist} from 'zustand/middleware';
import type {User, Session} from '../auth';
import {authClient} from '../auth';
import {queryClient} from '@common-web/lib/trpc';
import {AUTH_STORAGE_KEY} from '@common/constants/storage';
import {reportError} from '@common-web/lib/errorReporting';
import {useViewAsStore} from '@common-web/stores/viewAsStore';

export function hasPortalAccessProfile(user: User): boolean {
  return !!(
    user.cbsInternalProfile ||
    user.clientProfile ||
    user.brokerProfile ||
    user.tpaProfile ||
    (user.groupProfiles && user.groupProfiles.length > 0)
  );
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => Promise<void>;
  setAuth: (user: User | null, session: Session | null) => void;
  checkAuth: () => Promise<void>;
  /** Re-fetch session/user from the API (e.g. after profile edits). Does not clear the store on network errors. */
  refreshUser: () => Promise<void>;
  isGroupUser: () => boolean;
  isClientUser: () => boolean;
  isBrokerUser: () => boolean;
  isInternalUser: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        session: null,
        isLoading: true,
        isGroupUser: () => {
          const user = get().user;
          // User has groupProfiles but NOT clientProfile
          return (
            !!(user?.groupProfiles && user.groupProfiles.length > 0 && !user.clientProfile) || false
          );
        },
        isBrokerUser: () => {
          const user = get().user;
          // User has brokerProfile but NOT clientProfile or groupProfiles
          return (
            !!(
              user?.brokerProfile &&
              !user.clientProfile &&
              (!user.groupProfiles || user.groupProfiles.length === 0)
            ) || false
          );
        },
        isClientUser: () => {
          const user = get().user;
          // User has clientProfile but NOT groupProfiles (or empty)
          return (
            !!(user?.clientProfile && (!user.groupProfiles || user.groupProfiles.length === 0)) ||
            false
          );
        },
        isInternalUser: () => {
          const user = get().user;
          return !!user?.cbsInternalProfile;
        },
        setUser: (user) => set({user}),
        setSession: (session) => set({session}),
        setLoading: (loading) => set({isLoading: loading}),
        logout: async () => {
          queryClient.clear();
          useViewAsStore.getState().clearViewAs();
          try {
            await authClient.signOut();
            set({user: null, session: null});
          } catch (error) {
            console.error('Logout error:', error);
            // Still clear local state even if server logout fails
            set({user: null, session: null});
          }
        },
        setAuth: (user, session) => set({user, session, isLoading: false}),
        checkAuth: async () => {
          try {
            const result = await authClient.getSession();
            if (result.data) {
              const user = result.data.user as User;
              if (!hasPortalAccessProfile(user)) {
                useViewAsStore.getState().clearViewAs();
                set({user: null, session: null, isLoading: false});
                return;
              }
              set({
                user,
                session: result.data.session as Session,
                isLoading: false,
              });
            } else {
              useViewAsStore.getState().clearViewAs();
              set({user: null, session: null, isLoading: false});
            }
          } catch (error) {
            console.error('Auth check error:', error);
            useViewAsStore.getState().clearViewAs();
            set({user: null, session: null, isLoading: false});
          }
        },
        refreshUser: async () => {
          try {
            const result = await authClient.getSession();
            if (result.data) {
              const user = result.data.user as User;
              if (!hasPortalAccessProfile(user)) {
                useViewAsStore.getState().clearViewAs();
                set({user: null, session: null, isLoading: false});
                return;
              }
              set({
                user,
                session: result.data.session as Session,
                isLoading: false,
              });
            } else {
              useViewAsStore.getState().clearViewAs();
              set({user: null, session: null, isLoading: false});
            }
          } catch (error) {
            // Background session refresh — surfacing a toast here would be
            // noisy (e.g. on load when unauthenticated). Log only.
            reportError(error, {silent: true, context: 'authStore.refreshUser'});
          }
        },
      }),
      {
        name: AUTH_STORAGE_KEY,
        partialize: (state) => ({user: state.user, session: state.session}),
      },
    ),
  ),
);
